import { AnimatePresence, motion } from "framer-motion"
import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from 'react'
import useAuthStore from "../../store/useAuthStore";
import useLayoutStore from "../../store/useLayoutStore";
import useAppStore from "../../store/useAppStore";

const ChangeShiftStatusModal = () => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const setIsWorkPresenceFormOpen = useAppStore(state => state.setIsWorkPresenceFormOpen)
    const userId = useAuthStore.getState().userId
    const currentActive = useAppStore(state => state.employeesState[userId].currentActive)
    const [selectedCheckInRoom, setSelectedCheckInRoom] = useState("Online")
    const [selectedEmployee, setSelectedEmployee] = useState(null)
    const [openDropdownId, setOpenDropdownId] = useState(null)
    const modalContainerRef = useRef(null)
    const modalTitle = currentActive ? "Odjava sa radnog mesta" : "Prijava na radno mesto"

    useEffect(() => {
        const handleClickOutside = (e) => {
            // Ako je klik na modal container (600x400 div), ne zatvaraj modal
            if (modalContainerRef.current && modalContainerRef.current.contains(e.target)) {
                return;
            }

            setIsWorkPresenceFormOpen(false);
        };

        document.addEventListener('mousedown', handleClickOutside, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [setIsWorkPresenceFormOpen]);

    const modalContent = (
        <>
            <div
                className={`profile-modal-bg-wrapper ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}

                style={{ pointerEvents: 'none' }}
            >
                <motion.div
                    ref={modalContainerRef}
                    className={`change-shift-status-container ${isDarkTheme ? "dark-change-shift-status-container" : "white-change-shift-status-container"}`}
                    onClick={(e) => e.stopPropagation()}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{
                        opacity: 0,
                        x: -100,
                        transition: { delay: 0, duration: 0.14, ease: "easeInOut" }
                    }}
                    transition={{ duration: 0.19, delay: 0.14, ease: "easeInOut" }}
                >
                    <div className="cssm-close-buttons-container">
                        <button
                            type="button"
                            className={`cssm-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                            onClick={() => setIsWorkPresenceFormOpen(false)}
                            aria-label="Close modal"
                        >
                            <svg
                                width="28"
                                height="28"
                                viewBox="0 0 28 28"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ display: "block", margin: "auto" }}
                            >
                                <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                    <h3>{modalTitle}</h3>
                    <p>Izaberite nacin evidentiranja prisustva</p>

                    <CheckInRoomDropdown
                        dropdownId="checkInRoom"
                        openDropdownId={openDropdownId}
                        setOpenDropdownId={setOpenDropdownId}
                        selectedCheckInRoom={selectedCheckInRoom}
                        setSelectedCheckInRoom={setSelectedCheckInRoom}
                    />
                    <p>Izaberite osobu koja ce potvrditi ulazak ili izlazak...</p>
                    <CheckInConfirmationEmployeeDropdown
                        dropdownId="confirmationEmployee"
                        openDropdownId={openDropdownId}
                        setOpenDropdownId={setOpenDropdownId}
                        selectedEmployee={selectedEmployee}
                        setSelectedEmployee={setSelectedEmployee}
                    />

                    <StartEndShiftButton
                        selectedCheckInRoom={selectedCheckInRoom}
                        selectedEmployee={selectedEmployee}
                    />
                </motion.div>
            </div>
        </>
    )

    return createPortal(modalContent, document.body);
}

const CheckInRoomDropdown = ({ dropdownId, openDropdownId, setOpenDropdownId, selectedCheckInRoom, setSelectedCheckInRoom }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const checkInDeviceRooms = useAppStore(state => state.checkInDeviceRooms)
    const loadingCheckInDeviceRooms = useAppStore(state => state.loadingCheckInDeviceRooms)
    const userId = useAuthStore.getState().userId
    const currentActive = useAppStore(state => state.employeesState[userId].currentActive)
    const isOpen = openDropdownId === dropdownId

    const roomOptions = Array.isArray(checkInDeviceRooms) ? checkInDeviceRooms : [];
    const actionLabel = currentActive ? "Odjava" : "Prijava";
    const roomSuffix = currentActive ? "iz firme" : "u firmi";
    const displayValue = selectedCheckInRoom === "Online"
        ? `Online ${actionLabel.toLowerCase()}`
        : `${selectedCheckInRoom} (${actionLabel} ${roomSuffix})`;
    const displayIcon = selectedCheckInRoom === "Online" ? "./sidebar/Office.svg" : "./sidebar/Ekran.svg";

    const handleSelect = (room) => {
        setSelectedCheckInRoom(room);
        setOpenDropdownId(null);
    };

    return (
        <div
            className={`cssm-room-dropdown ${loadingCheckInDeviceRooms ? "cssm-room-dropdown--loading" : ""}`}
            onClick={() => setOpenDropdownId(isOpen ? null : dropdownId)}
        >
            <div className="cssm-room-dropdown__value">
                <AnimatePresence mode="wait" initial={false}>
                    <motion.img
                        key={`checkin-room-icon-${selectedCheckInRoom}-${displayValue}`}
                        src={displayIcon}
                        alt=""
                        className={`cssm-room-dropdown__presence-icon ${isDarkTheme ? "dark-filter" : ""}`}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                        style={{ transformOrigin: "center center" }}
                    />
                </AnimatePresence>
                <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                        key={`checkin-room-value-${displayValue}`}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                        style={{ transformOrigin: "center center", display: "inline-block" }}
                    >
                        {displayValue}
                    </motion.span>
                </AnimatePresence>
                {loadingCheckInDeviceRooms && <span className="cssm-room-dropdown__spinner" aria-hidden="true" />}
            </div>

            <div className="cssm-room-dropdown__arrow">
                <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.14, ease: "easeOut" }}
                    style={{ transformOrigin: "center" }}
                >
                    <svg viewBox="0 0 24 24" width="30" height="30" style={{ display: "block" }}>
                        <path
                            d="M7 10l5 5 5-5"
                            fill="none"
                            stroke={isDarkTheme ? "#e2e1e1ff" : "black"}
                            strokeWidth="1.9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </motion.div>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="cssm-room-dropdown__list"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                    >
                        <div
                            className="cssm-room-dropdown__item"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelect("Online");
                            }}
                        >
                            <div className="cssm-room-dropdown__presence-item-content">
                                <span>{`Online ${actionLabel.toLowerCase()}`}</span>
                                <img src="./sidebar/Office.svg" alt="" className={`cssm-room-dropdown__presence-icon ${isDarkTheme ? "dark-filter" : ""}`} />
                            </div>
                        </div>

                        {loadingCheckInDeviceRooms && (
                            <div className="cssm-room-dropdown__item cssm-room-dropdown__item--loading">
                                <span>Ucitavanje...</span>
                            </div>
                        )}

                        {roomOptions.map((room) => (
                            <div
                                key={room}
                                className="cssm-room-dropdown__item"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelect(room);
                                }}
                            >
                                <div className="cssm-room-dropdown__presence-item-content">
                                    <span>{`${room} (${actionLabel} ${roomSuffix})`}</span>
                                    <img src="./sidebar/Ekran.svg" alt="" className={`cssm-room-dropdown__presence-icon ${isDarkTheme ? "dark-filter" : ""}`} />
                                </div>
                            </div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

const CheckInConfirmationEmployeeDropdown = ({ dropdownId, openDropdownId, setOpenDropdownId, selectedEmployee, setSelectedEmployee }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const employeesData = useAppStore(state => state.employeesData)
    const isOpen = openDropdownId === dropdownId

    const employeesList = employeesData
        ? Object.entries(employeesData).map(([userId, employee]) => ({
            userId,
            fullName: `${employee.ime} ${employee.prezime}`,
            image: employee.image
        }))
        : [];

    const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
    const displayName = selectedEmployee ? selectedEmployee.fullName : "Bez potvrde";
    const displayImage = selectedEmployee ? `${baseImgUrl}/employees/${selectedEmployee.image}` : null;

    const handleSelect = (employee) => {
        setSelectedEmployee(employee);
        setOpenDropdownId(null);
    };

    return (
        <div className="cssm-room-dropdown" onClick={() => setOpenDropdownId(isOpen ? null : dropdownId)}>
            <div className="cssm-room-dropdown__value">
                <AnimatePresence mode="wait" initial={false}>
                    {displayImage ? (
                        <motion.img
                            key={`confirm-employee-image-${displayImage}`}
                            src={displayImage}
                            alt=""
                            className="cssm-room-dropdown__employee-img"
                            crossOrigin="use-credentials"
                            onError={(e) => {
                                e.target.src = "./views/login.svg";
                            }}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ duration: 0.14, ease: "easeOut" }}
                        />
                    ) : (
                        <motion.img
                            key="confirm-employee-image-default"
                            src="./views/NijeTu.svg"
                            alt=""
                            className={`cssm-room-dropdown__employee-img cssm-room-dropdown__employee-img--default ${isDarkTheme ? "dark-filter" : ""}`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ duration: 0.14, ease: "easeOut" }}
                        />
                    )}
                </AnimatePresence>
                <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                        key={`confirm-employee-name-${displayName}`}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                    >
                        {displayName}
                    </motion.span>
                </AnimatePresence>
            </div>

            <div className="cssm-room-dropdown__arrow">
                <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.14, ease: "easeOut" }}
                    style={{ transformOrigin: "center" }}
                >
                    <svg viewBox="0 0 24 24" width="30" height="30" style={{ display: "block" }}>
                        <path
                            d="M7 10l5 5 5-5"
                            fill="none"
                            stroke={isDarkTheme ? "#e2e1e1ff" : "black"}
                            strokeWidth="1.9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </motion.div>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="cssm-room-dropdown__list"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                    >
                        <div
                            className="cssm-room-dropdown__item cssm-room-dropdown__employee-item"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelect(null);
                            }}
                        >
                            <img
                                src="./views/NijeTu.svg"
                                alt=""
                                className={`cssm-room-dropdown__employee-item-img ${isDarkTheme ? "dark-filter" : ""}`}
                            />
                            <span>Bez potvrde</span>
                        </div>

                        {employeesList.map((employee) => (
                            <div
                                key={employee.userId}
                                className="cssm-room-dropdown__item cssm-room-dropdown__employee-item"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelect(employee);
                                }}
                            >
                                <img
                                    src={`${baseImgUrl}/employees/${employee.image}`}
                                    alt=""
                                    className="cssm-room-dropdown__employee-item-img"
                                    crossOrigin="use-credentials"
                                    onError={(e) => {
                                        e.target.src = "./views/login.svg";
                                    }}
                                />
                                <span>{employee.fullName}</span>
                            </div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

const StartEndShiftButton = ({ selectedCheckInRoom, selectedEmployee }) => {
    const userId = useAuthStore.getState().userId
    const currentActive = useAppStore(state => state.employeesState[userId].currentActive)
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const handleWorkCheckIn = useAppStore(state => state.handleWorkCheckIn)
    const setIsWorkPresenceFormOpen = useAppStore(state => state.setIsWorkPresenceFormOpen)

    const statusText = currentActive ? "Izlaz" : "Ulaz"

    const handleStatusClick = async () => {
        setIsWorkPresenceFormOpen(false)
        await handleWorkCheckIn({
            checkInRoom: selectedCheckInRoom,
            confirmationEmployeeId: selectedEmployee?.userId || null
        })
    }

    return (
        <button
            className={`start-end-shift-button cssm-start-end-shift-button ${currentActive ? "cssm-start-end-shift-button--logout" : "cssm-start-end-shift-button--login"} ${isDarkTheme ? "cssm-start-end-shift-button--dark" : "cssm-start-end-shift-button--light"}`}
            onClick={handleStatusClick}
        >
            <span>{statusText}</span>
        </button>
    )
}

export default ChangeShiftStatusModal

